import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Instagram, Pencil, Trash2 } from "lucide-react";
import api, { fileUrl, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loading, Empty, Modal, Uploader, Avatar } from "@/components/atoms";

const EMPTY = { name: "", instagram: "", bio: "", photo_url: "" };

export default function Artists() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [artists, setArtists] = useState(null);
  const [editing, setEditing] = useState(null); // {mode, data}

  const load = async () => {
    try { const { data } = await api.get("/artists"); setArtists(data); } catch (e) { setArtists([]); }
  };
  useEffect(() => { load(); }, []);

  const openInstagram = (handle) => {
    const h = handle.replace("@", "").trim();
    window.open(`https://instagram.com/${h}`, "_blank");
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing.mode === "edit") await api.put(`/admin/artists/${editing.data.id}`, editing.data);
      else await api.post("/admin/artists", editing.data);
      toast.success("Saved");
      setEditing(null);
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const del = async (id) => {
    try { await api.delete(`/admin/artists/${id}`); load(); toast.success("Removed"); } catch (e) { toast.error("Failed"); }
  };

  const upd = (k) => (e) => setEditing({ ...editing, data: { ...editing.data, [k]: e.target.value } });

  return (
    <div data-testid="artists-page">
      <div className="px-5 pt-5 flex items-center justify-between">
        <div>
          <span className="eyebrow">The Crew</span>
          <h1 className="font-display font-black text-3xl uppercase leading-tight">Artists</h1>
        </div>
        {isAdmin && (
          <button className="atc-btn btn-gold px-3 h-11" onClick={() => setEditing({ mode: "add", data: { ...EMPTY } })} data-testid="artist-add-btn">
            <Plus size={18} /> Add
          </button>
        )}
      </div>

      {artists === null ? <Loading /> : artists.length === 0 ? <Empty title="No artists listed" /> : (
        <div className="px-5 py-4 flex flex-col gap-4">
          {artists.map((a) => (
            <div key={a.id} className="atc-card p-4 flex gap-4 rise-in" data-testid={`artist-card-${a.id}`}>
              <Avatar src={a.photo_url} name={a.name} size={64} />
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-lg leading-tight">{a.name}</h3>
                <button className="font-mono2 text-xs text-[#8B1E28] flex items-center gap-1" onClick={() => openInstagram(a.instagram)} data-testid={`artist-ig-${a.id}`}>
                  <Instagram size={13} /> {a.instagram}
                </button>
                <p className="text-sm text-[#554E40] mt-1">{a.bio}</p>
                {isAdmin && (
                  <div className="flex gap-2 mt-3">
                    <button className="atc-btn btn-outline h-9 px-3 text-xs" onClick={() => setEditing({ mode: "edit", data: { ...a } })} data-testid={`artist-edit-${a.id}`}><Pencil size={13} /> Edit</button>
                    <button className="atc-btn btn-primary h-9 px-3 text-xs" onClick={() => del(a.id)} data-testid={`artist-delete-${a.id}`}><Trash2 size={13} /> Delete</button>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={!!editing} onClose={() => setEditing(null)} title={editing?.mode === "edit" ? "Edit Artist" : "Add Artist"} testid="artist-modal">
        {editing && (
          <form onSubmit={save} className="flex flex-col gap-3">
            {editing.data.photo_url && <img src={fileUrl(editing.data.photo_url)} alt="preview" className="w-24 h-24 object-cover border-2 border-black rounded-full mx-auto" />}
            <input className="atc-input" placeholder="Name" value={editing.data.name} onChange={upd("name")} required data-testid="artist-name" />
            <input className="atc-input" placeholder="Instagram (@handle)" value={editing.data.instagram} onChange={upd("instagram")} required data-testid="artist-instagram" />
            <textarea className="atc-input" rows={3} placeholder="Bio" value={editing.data.bio} onChange={upd("bio")} required data-testid="artist-bio" />
            <input className="atc-input" placeholder="Photo URL" value={editing.data.photo_url} onChange={upd("photo_url")} data-testid="artist-photo-url" />
            <Uploader onUploaded={(url) => setEditing({ ...editing, data: { ...editing.data, photo_url: url } })} label="Or upload photo" testid="artist-upload" />
            <button type="submit" className="atc-btn btn-primary" data-testid="artist-save-btn">Save Artist</button>
          </form>
        )}
      </Modal>
    </div>
  );
}
